<script setup lang="ts">
import { ProductAvailability } from './ProductAvailability.vue'

type ProductLeadTimeCallOutProps = {
  availability: ProductAvailability
  pdpDeliveryCallout?: boolean
  hasMakeToOrderItems?: boolean
  isPdp: boolean
  // My Bag & Checkout presentation; PDP and the quick-add quickview keep the original.
  isCartStyle?: boolean
}
const props = defineProps<ProductLeadTimeCallOutProps>()
const uiStore = useUIStore()
const showModal = ref(false)
const showSpecialOrderInfo = ref(false)
const preferencesStore = usePreferencesStore()
const { bopisEnabled } = useFeatureFlags()
const madeToOrderLeadTimeCallout = preferencesStore.pdp?.madeToOrderLeadTimeCallout
const dispatchAndProcessingLeadTimeCallout = preferencesStore.pdp?.dispatchAndProcessingLeadTimeCallout

const isBopisPdp = computed(() => bopisEnabled() && props.isPdp)
const isBopisCart = computed(() => bopisEnabled() && props.isCartStyle)

const isShowPopup = computed(
  () =>
    // Suppresses the info popup inside the content-action quick-add quickview (avoids a
    // modal-in-modal there), but the BOPIS My Bag & Checkout edit panel still wants it.
    (!uiStore.isShowQuickviewModal || isBopisCart.value) &&
    props.availability.icon !== 'ready' &&
    (props.availability?.callout || props.availability?.madeToOrderInfo || props.availability?.dispatchLeadTime),
)

const iconOnRight = computed(
  () => isBopisPdp.value && (props.availability.icon === 'resizedForYou' || props.availability.icon === 'madeToOrder'),
)
const hideResizedLeadTime = computed(() => isBopisPdp.value && props.availability.icon === 'resizedForYou')

const openProductInfoModal = () => {
  showModal.value = true
  showSpecialOrderInfo.value = false
}

const openSpecialOrderInfoModal = () => {
  showModal.value = true
  showSpecialOrderInfo.value = true
}
</script>

<template>
  <div :class="['product-lead-time', { 'product-lead-time--pdp': isPdp }]">
    <template v-if="availability && !pdpDeliveryCallout">
      <div
        class="product-lead-time__message"
        :class="{ 'product-lead-time__message--icon-right': iconOnRight }"
        data-testid="product-lead-time-call-out__message"
      >
        <span class="product-lead-time__text" data-testid="product-lead-time-call-out__text">
          <!-- Made to Order -->
          <template v-if="availability.icon === 'madeToOrder'">
            <svg-icon
              v-if="!isBopisCart"
              name="mhj:made-for-you"
              class="product-lead-time__message-icon product-lead-time__message-icon--mfy"
            />
            <span
              ><strong>{{ $t('product.lead_time_callout.made_to_order') }}</strong> - {{ availability.message }}.</span
            >
          </template>
          <!-- Resized For You -->
          <template v-else-if="availability.icon === 'resizedForYou'">
            <svg-icon v-if="!isBopisCart" name="mhj:clock" class="product-lead-time__message-icon" />
            <span>
              <strong>{{ $t('product.lead_time_callout.resized_for_you') }}</strong>
              <template v-if="!hideResizedLeadTime"> - {{ availability.message }}</template>
            </span>
          </template>

          <!-- Engraved, or plain dispatch with no item-type label -->
          <template v-else-if="availability.icon === 'engraved' || availability.icon === 'dispatch'">
            <svg-icon v-if="!isBopisCart" name="mhj:clock" class="product-lead-time__message-icon" />
            <span> {{ availability.message }} </span>
          </template>

          <!-- Everything else -->
          <template v-else>
            <svg-icon
              v-if="availability.icon === 'ready'"
              :name="isBopisCart ? 'mhj:tick-wo-circle' : 'mhj:green-tick'"
              class="product-lead-time__message-icon product-lead-time__message-icon--ready"
              data-testid="product-lead-time-call-out__ready-icon"
            />
            <strong>
              {{ availability.message }}
            </strong>
          </template>
        </span>
        <!-- BOPIS My Bag & Checkout wants underlined "More info" text; elsewhere keeps the icon. -->
        <Button
          v-if="isShowPopup"
          variant="text"
          :class="{ 'product-lead-time__more-info-link': isBopisCart }"
          data-testid="product-lead-time-call-out__more-info"
          @click="openProductInfoModal"
        >
          <template v-if="isBopisCart">{{ $t('product.lead_time_callout.link') }}</template>
          <svg-icon v-else name="mhj:more-info" class="product-lead-time__more-info-icon" />
        </Button>
      </div>

      <div v-if="availability.showMTOCancellationMsg && !isBopisCart" class="product-lead-time__cancellation">
        {{ $t('product.product_availability.no_cancellations_or_returns') }}
        {{ $t('product.product_availability.for_mto_products') }}
      </div>
    </template>

    <!-- For MyBag / Order Summary -->
    <div v-if="hasMakeToOrderItems && !pdpDeliveryCallout" class="product-lead-time__special-order-info">
      {{ $t('my_bag.special_order_info.pretext') }}
      <Button variant="text" @click="openSpecialOrderInfoModal">
        {{ $t('my_bag.special_order_info.link') }}
      </Button>
      {{ $t('my_bag.special_order_info.posttext') }}
    </div>

    <div v-if="pdpDeliveryCallout" class="product-lead-time__delivery">
      <div v-if="availability.icon !== 'ready'" class="product-lead-time__message product-lead-time__message--bold">
        {{ $t(`product.product_availability.availability_message_label.icon_keys.${availability.icon}`) }}
        <Button variant="text" @click="openProductInfoModal">
          <svg-icon name="mhj:circle-question" class="product-lead-time__more-info-icon" />
        </Button>
      </div>
      <span
        class="product-lead-time__message"
        :class="{ 'product-lead-time__message--bold': availability.icon === 'ready' }"
        >{{ availability.message }}</span
      >
      <span class="product-lead-time__stock">{{ availability.onlineStockIndicator }}</span>
    </div>

    <Modal variant="slide" :show="showModal" @close="showModal = false">
      <template v-if="availability?.callout?.fields.content">
        <RichTextPanel :content="availability.callout.fields.content" skip-container />
      </template>
      <template
        v-else-if="
          (props.availability?.madeToOrderInfo && !props.availability?.dispatchLeadTime) || showSpecialOrderInfo
        "
      >
        <RichTextPanel
          v-if="madeToOrderLeadTimeCallout?.fields?.content"
          :content="madeToOrderLeadTimeCallout.fields.content"
        />
      </template>
      <template v-else>
        <RichTextPanel
          v-if="dispatchAndProcessingLeadTimeCallout?.fields?.content"
          :content="dispatchAndProcessingLeadTimeCallout.fields.content"
        />
      </template>
    </Modal>
  </div>
</template>

<style lang="postcss">
.product-lead-time {
  font-size: var(--text-xsmall);
  display: flex;
  flex-direction: column;
  gap: var(--space-20);

  .product-lead-time__message {
    font-size: var(--text-small);
    text-align: center;
    display: inline-flex;
    gap: var(--space-20);
    flex-wrap: wrap;
    white-space: nowrap;
    align-items: center;

    &.product-lead-time__message--bold {
      font-weight: bold;
      text-decoration: none;
    }

    &.product-lead-time__message--icon-right {
      flex-wrap: nowrap;
      width: 100%;
      padding-right: var(--space-60);

      .product-lead-time__text {
        display: inline-flex;
        align-items: center;
        gap: var(--space-20);
        flex-wrap: wrap;
      }

      > button {
        position: absolute;
        top: 50%;
        right: var(--space-40);
        transform: translateY(-50%);
      }
    }

    .product-lead-time__text {
      display: contents;
    }

    .product-lead-time__message-icon,
    .product-lead-time__more-info-icon {
      height: var(--space-50);
      padding: 1px;
      &.product-lead-time__message-icon--ready {
        color: var(--status-success);
      }
    }

    /* Flushes the button's text with the sibling plain text instead of centring a padded box. */
    .product-lead-time__more-info-link {
      padding: 0;
      border: none;
      background: none;
      line-height: inherit;
      font-size: inherit;
    }
  }

  .product-lead-time__cancellation {
    margin-bottom: var(--space-10);
  }

  button {
    font-size: var(--text-xsmall);
    text-transform: none;
  }

  .product-lead-time__delivery {
    display: flex;
    flex-direction: column;
    gap: var(--space-20);

    button,
    .product-lead-time__message {
      font-size: var(--text-base);
    }

    .product-lead-time__stock {
      font-size: var(--text-base);
      font-weight: bold;
      color: var(--status-success);
    }
  }

  .product-lead-time__special-order-info {
    margin-top: var(--space-10);
    margin-bottom: var(--space-10);
  }
}

.product-lead-time:not(.product-lead-time--pdp) {
  text-align: center;

  .product-lead-time__message {
    justify-content: center;
  }
}
</style>
